import { Check, Circle, type LucideIcon, Minus, X } from "lucide-react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Stack from "@mui/material/Stack";

import { Glare } from "@src/components/animation/Glare";
import { cn } from "@src/lib/utils";

/**
 * One step of something that runs in steps.
 *
 * - `done`: it happened.
 * - `skipped`: it looked and there was nothing there. Nothing went wrong, so
 *   nothing is coloured.
 * - `failed`: it went wrong. The only coloured state, so it stands out on
 *   exactly the runs where it matters.
 */
export interface ProgressStep {
  id: string;
  label: string;
  status: "pending" | "running" | "done" | "skipped" | "failed";
  /** Why it was skipped or failed, or what a running step waits for. */
  detail?: string;
  /** What the step is about. */
  icon?: LucideIcon;
}

/**
 * Steps as plain rows: an icon and a line of text each, for steps reported
 * inside something else, such as an AI tool card. A running row glares.
 */
export const StepRows = ({
  steps,
  compact,
  className,
}: {
  steps: ProgressStep[];
  compact?: boolean;
  /**
   * Merged last, so a surface can change the block's inset. The padding is
   * Tailwind rather than `sx` for exactly that reason: an `sx` value and a
   * caller's utility class are both a single class, so which one won would come
   * down to stylesheet order. Through `cn` the caller simply wins.
   */
  className?: string;
}) => (
  <Stack
    spacing={compact ? 1 : 1.5}
    className={cn(compact ? "px-2.5" : "px-3", "py-2", className)}
  >
    {steps.map((step) => {
      const row = (
        <Stack direction="row" spacing={2} alignItems="center">
          <RowIcon step={step} />
          <IgTypography fontSize="12px">{step.label}</IgTypography>
          {step.detail ? (
            <IgTypography fontSize="12px" sx={{ color: "text.secondary" }}>
              — {step.detail}
            </IgTypography>
          ) : null}
        </Stack>
      );
      return (
        <Stack
          key={step.id}
          direction="row"
          data-testid="step"
          data-status={step.status}
          sx={{
            // A queued row is dimmed further than a finished one: it is the
            // only state that has not happened yet, and it should read as the
            // plan rather than as part of the account.
            opacity:
              step.status === "pending"
                ? 0.45
                : step.status === "failed" || step.status === "skipped"
                  ? 0.6
                  : 1,
          }}
        >
          {step.status === "running" ? <Glare>{row}</Glare> : row}
        </Stack>
      );
    })}
  </Stack>
);

/**
 * The step's own icon says what it is about; the status glyph says whether it
 * worked. When both apply the status wins: a failure matters more than its
 * topic.
 */
const RowIcon = ({ step }: { step: ProgressStep }) => {
  if (step.status === "failed") {
    return <X size={14} className="text-destructive" />;
  }
  if (step.status === "skipped") {
    return <Minus size={14} className="text-muted-foreground" />;
  }
  if (step.icon && (step.status === "done" || step.status === "running")) {
    return <step.icon size={14} className="text-muted-foreground" />;
  }
  if (step.status === "done") {
    return <Check size={14} className="text-muted-foreground" />;
  }
  // Queued. A hollow ring rather than the running state's blank, so a row that
  // has not started is distinguishable from one that has.
  if (step.status === "pending") {
    return (
      <Circle
        size={14}
        className="text-muted-foreground"
        strokeWidth={1.5}
        aria-hidden
      />
    );
  }
  return <span className="inline-block size-[14px]" />;
};
